import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { credentialsApi, } from "../../api/credentials.api.js";
import { unwrapError } from "../../api/client.js";
import { Card, Pill, GhostButton, DangerButton, MiniSpinner, Icon, colors, } from "../ui/index.js";
import { useToast } from "../ui/ToastProvider.js";
import { useConfirm } from "../ui/ConfirmProvider.js";
import { venueShortNames } from "../../lib/venue-names.js";
const TEST_COOLDOWN_MS = 30_000;
const STATUS_LABEL = {
    valid: "Valid",
    invalid_creds: "Invalid credentials",
    locked: "Locked",
    rate_limited: "Rate limited",
    error: "Error",
};
const STATUS_COLOR = {
    valid: colors.success,
    invalid_creds: colors.danger,
    locked: colors.danger,
    rate_limited: colors.warning,
    error: colors.danger,
};
function humaniseTimestamp(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    const diffMs = Date.now() - d.getTime();
    const diffSec = Math.round(diffMs / 1000);
    if (diffSec < 60)
        return "just now";
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60)
        return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24)
        return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7)
        return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
const labelCellStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flex: 1,
    minWidth: 200,
};
const metaRowStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    fontSize: "0.8rem",
    color: colors.textSecondary,
    marginTop: 8,
};
const buttonRowStyle = {
    display: "flex",
    gap: 8,
    alignItems: "center",
};
export function CredentialRow({ cred, onDelete }) {
    const qc = useQueryClient();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [lastTestedAt, setLastTestedAt] = useState(null);
    const test = useMutation({
        mutationFn: () => credentialsApi.test(cred.id),
        onSuccess: () => {
            setLastTestedAt(Date.now());
            showToast(`${cred.label} validated successfully`, "success");
            void qc.invalidateQueries({ queryKey: ["credentials"] });
        },
        onError: async (err) => {
            setLastTestedAt(Date.now());
            const apiErr = await unwrapError(err);
            showToast(`Test failed: ${apiErr.message}`, "error");
            void qc.invalidateQueries({ queryKey: ["credentials"] });
        },
    });
    const cooldownActive = lastTestedAt !== null && Date.now() - lastTestedAt < TEST_COOLDOWN_MS;
    const testDisabled = test.isPending || cooldownActive;
    const handleDelete = async () => {
        const ok = await confirm("Remove credential?", `This will permanently remove "${cred.label}". Future bookings using this credential will be skipped.`, { danger: true, confirmText: "Remove" });
        if (ok) {
            onDelete();
        }
    };
    const statusColor = STATUS_COLOR[cred.status];
    return (_jsx(Card, { children: _jsxs("div", { style: { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }, children: [_jsxs("div", { style: labelCellStyle, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }, children: [_jsx("strong", { style: { fontSize: "0.95rem" }, children: cred.username }), _jsx(Pill, { color: statusColor, children: STATUS_LABEL[cred.status] })] }), _jsxs("div", { style: metaRowStyle, children: [_jsxs("span", { children: [_jsx(Icon, { name: "clock", size: 12 }), " Last tested ", humaniseTimestamp(cred.lastValidatedAt)] }), _jsxs("span", { children: [cred.credits, " credit", cred.credits === 1 ? "" : "s"] }), _jsx("span", { children: cred.accessibleVenues.length > 0
                                        ? `Venues: ${venueShortNames(cred.accessibleVenues)}`
                                        : "No accessible venues" })] }), cred.lastError && (_jsx("div", { style: {
                                marginTop: 8,
                                padding: 8,
                                borderRadius: 6,
                                background: colors.dangerLight,
                                color: colors.danger,
                                fontSize: "0.8rem",
                            }, children: cred.lastError }))] }), _jsxs("div", { style: buttonRowStyle, children: [_jsx(GhostButton, { onClick: () => test.mutate(), disabled: testDisabled, children: test.isPending ? (_jsxs(_Fragment, { children: [_jsx(MiniSpinner, { size: 12 }), " Testing"] })) : cooldownActive ? ("Wait 30s") : ("Test login") }), _jsxs(DangerButton, { onClick: () => void handleDelete(), children: [_jsx(Icon, { name: "trash", size: 12 }), " Remove"] })] })] }) }));
}
